import {inject, Injectable} from '../di';
import {Logger} from '../logger';
import loaderTemplate from './loader.html';
import {LegacyLogService} from '../logger/legacy-log-service';

/**
 * Service to manage the application loader (spinner displayed during loading pages)
 */
@Injectable()
export class LoaderService {
  private readonly logger = inject(Logger);
  private readonly legacyLogService = inject(LegacyLogService);

  private readonly container = document.body;

  private isRunning: boolean = false;

  private noop = () => {};

  private get isSystemStarted(): boolean {
    // TODO refactor legacy
    return !!window.system_started;
  }

  constructor() {
    window.Loader = this;
  }

  /**
   * Start the loader
   * When loader is active, keydown events are disabled
   * @param delay
   */
  public start(delay?: boolean) {
    try {
      if (this.isRunning) {
        this.legacyLogService.add('Loader already exist');
        this.logger.warn('[LoaderService] Unable to start Loader, it was already started');
        return;
      }

      if (!this.isSystemStarted) {
        this.logger.warn('[LoaderService] Failed to start Loader, the system is not ready yet');
        return;
      }

      this.manageKeydown(true);

      this.isRunning = true;

      this.container.insertAdjacentHTML('beforeend', loaderTemplate as string);

      const loaderEl = $('#loader');
      if (delay) {
        setTimeout(() => {
          loaderEl.show();
        }, 300);
      } else {
        loaderEl.show();
      }
    } catch (err) {
      this.logger.error('[LoaderService] Loader failed to start due to an unexpected error.', err);
    }
  }

  /**
   * Stop the loader
   */
  public stop() {
    try {
      if (!this.isSystemStarted) {
        this.logger.warn('[LoaderService] Failed to stop Loader, the system is not ready yet');
        return;
      }

      if (!Loader.isRunning) {
        this.logger.warn('[LoaderService] Failed to stop Loader, no loader to stop');
        return;
      }

      this.manageKeydown(false);

      Loader.isRunning = false;

      const loaders = this.container.querySelectorAll('.loader');
      for (let i = 0; i < loaders.length; i++) {
        this.container.removeChild(loaders[i]);
      }
    } catch (err) {
      this.logger.error('[LoaderService] Loader failed to stop due to an unexpected error.', err);
    }
  }

  /**
   * Manage keydown event override
   * @param detach - should we disable keydown events
   */
  private manageKeydown(detach: boolean) {
    if (detach) {
      window.setCurrentTvKeydownOverride(this.noop);
    } else {
      window.removeCurrentTvKeydownOverride();
    }
  }
}

declare global {
  interface Window {
    Loader: LoaderService;
  }
  const Loader: LoaderService;
}
